import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import { CancelledError } from '@tanstack/react-query';
import routes from 'js/core_pre_webpack/routes';
import {
  AnalyticsAccessContext,
  AnalyticsV2Options,
  analyticsPreviewFromUrl,
  withAnalyticsPreview,
} from 'js/analytics_v2/options';
import {
  ActivityResponse,
  AnalyticsPeriod,
  AudienceResponse,
  LandingSections,
  ReaderCohortsResponse,
  ReaderCategory,
  ReaderFilteredCountsResponse,
  ReaderPreviewResponse,
  ReaderProfilesResponse,
  ReaderRankingsResponse,
  ReaderTotalCountResponse,
  TrafficLiveResponse,
  TrafficResponse,
  UniqueVisitorsResponse,
} from 'js/analytics_v2/types';

type Resource =
  | 'traffic'
  | 'traffic_live'
  | 'unique_visitors'
  | 'audience'
  | 'activity';

const resourcePaths = {
  traffic: routes.traffic_api_v0_user_analytics_v2_path,
  traffic_live: routes.traffic_live_api_v0_user_analytics_v2_path,
  unique_visitors: routes.unique_visitors_api_v0_user_analytics_v2_path,
  audience: routes.audience_api_v0_user_analytics_v2_path,
  activity: routes.activity_api_v0_user_analytics_v2_path,
};

export type AnalyticsV2EndpointBuilder = (
  resource: Resource,
  viewedUserId: number,
  period: AnalyticsPeriod,
) => string;

type ReaderCountResource = 'reader_total_count' | 'reader_filtered_counts';

export type ReaderCountEndpointBuilder = (
  resource: ReaderCountResource,
  viewedUserId: number,
  cacheOnly: boolean,
  options?: AnalyticsV2Options,
) => string;

export type ReaderCohortEndpointBuilder = (
  viewedUserId: number,
  options?: AnalyticsV2Options,
) => string;

export type ReaderPreviewEndpointBuilder = (
  viewedUserId: number,
  options?: AnalyticsV2Options,
) => string;

export type ReaderProfilesEndpointBuilder = (
  viewedUserId: number,
  category: ReaderCategory,
  cursor?: string,
  options?: AnalyticsV2Options,
) => string;

export type ReaderRankingsEndpointBuilder = (
  viewedUserId: number,
  options?: AnalyticsV2Options,
) => string;

export const buildAnalyticsV2Endpoint = (
  resource: Resource,
  viewedUserId: number,
  period: AnalyticsPeriod,
  options: AnalyticsV2Options = { fixtures: false, delay: false },
): string =>
  resourcePaths[resource]({
    user_id: viewedUserId,
    period,
    ...(options.fixtures ? { fixtures: true } : {}),
    ...(options.fixtures && options.delay ? { delay: true } : {}),
    ...(options.nonPremium ? { non_premium: true } : {}),
  });

export type AnalyticsRequest = {
  signal?: AbortSignal;
  preview?: AnalyticsAccessContext['analytics_preview'];
  isCurrent?: () => boolean;
  verify?: (context?: AnalyticsAccessContext) => void;
  upgrade?: (destination: string) => void;
};

export class AnalyticsUpgradeRequired extends Error {}

export async function fetchResource<T>(
  endpoint: string,
  request?: AnalyticsRequest,
  init?: RequestInit,
): Promise<T> {
  const contextualEndpoint = withAnalyticsPreview(
    endpoint,
    request ? (request.preview ?? undefined) : analyticsPreviewFromUrl(),
  );
  const assertCurrent = (): void => {
    if (request?.signal?.aborted || request?.isCurrent?.() === false)
      throw new CancelledError({ revert: true, silent: true });
  };
  try {
    assertCurrent();
    const response =
      init || request?.signal
        ? await fetchWithCsrf(contextualEndpoint, {
            ...init,
            signal: request?.signal,
          })
        : await fetchWithCsrf(contextualEndpoint);
    assertCurrent();
    if (!response.ok) {
      if (response.status === 403) {
        const body = await response.json();
        assertCurrent();
        const upgrade = body.errors?.find(
          (error: { code?: string }) =>
            error.code === 'analyticsUpgradeRequired',
        );
        if (upgrade && typeof upgrade.redirect_url === 'string') {
          request?.verify?.(body.analytics_context);
          request?.upgrade?.(upgrade.redirect_url);
          throw new AnalyticsUpgradeRequired('Analytics upgrade required');
        }
      }
      throw new Error(`Analytics request failed (${response.status})`);
    }
    const body = await response.json();
    assertCurrent();
    request?.verify?.(body.analytics_context);
    return body as T;
  } catch (error) {
    assertCurrent();
    throw error;
  }
}

export const fetchAnalyticsContext = (
  viewedUserId: number,
  options: AnalyticsV2Options = { fixtures: false, delay: false },
  request?: AnalyticsRequest,
): Promise<AnalyticsAccessContext> =>
  fetchResource<{ analytics_context: AnalyticsAccessContext }>(
    routes.context_api_v0_user_analytics_v2_path({
      user_id: viewedUserId,
      ...(options.fixtures ? { fixtures: true } : {}),
    }),
    request,
  ).then((response) => response.analytics_context);

const readerCountResourcePaths = {
  reader_total_count: routes.reader_total_count_api_v0_user_analytics_v2_path,
  reader_filtered_counts:
    routes.reader_filtered_counts_api_v0_user_analytics_v2_path,
};

export const buildReaderCountEndpoint: ReaderCountEndpointBuilder = (
  resource,
  viewedUserId,
  cacheOnly,
  options = { fixtures: false, delay: false },
): string =>
  readerCountResourcePaths[resource]({
    user_id: viewedUserId,
    ...(cacheOnly ? { cache_only: true } : {}),
    ...(options.fixtures ? { fixtures: true } : {}),
    ...(options.fixtures && options.delay ? { delay: true } : {}),
  });

export const buildReaderCohortEndpoint: ReaderCohortEndpointBuilder = (
  viewedUserId,
  options = { fixtures: false, delay: false },
): string =>
  routes.reader_cohorts_api_v0_user_analytics_v2_path({
    user_id: viewedUserId,
    ...(options.fixtures ? { fixtures: true } : {}),
    ...(options.fixtures && options.delay ? { delay: true } : {}),
  });

export const buildReaderPreviewEndpoint: ReaderPreviewEndpointBuilder = (
  viewedUserId,
  options = { fixtures: false, delay: false },
): string =>
  routes.reader_preview_api_v0_user_analytics_v2_path({
    user_id: viewedUserId,
    ...(options.fixtures ? { fixtures: true } : {}),
    ...(options.fixtures && options.delay ? { delay: true } : {}),
  });

export const buildReaderProfilesEndpoint: ReaderProfilesEndpointBuilder = (
  viewedUserId,
  category,
  cursor,
  options = { fixtures: false, delay: false },
): string =>
  routes.reader_profiles_api_v0_user_analytics_v2_path({
    user_id: viewedUserId,
    category,
    ...(cursor ? { cursor } : {}),
    ...(options.fixtures ? { fixtures: true } : {}),
    ...(options.fixtures && options.delay ? { delay: true } : {}),
  });

export const buildReaderRankingsEndpoint: ReaderRankingsEndpointBuilder = (
  viewedUserId,
  options = { fixtures: false, delay: false },
): string =>
  routes.reader_rankings_api_v0_user_analytics_v2_path({
    user_id: viewedUserId,
    ...(options.fixtures ? { fixtures: true } : {}),
    ...(options.fixtures && options.delay ? { delay: true } : {}),
  });

export const fetchTraffic = (
  viewedUserId: number,
  period: AnalyticsPeriod,
  endpointBuilder = buildAnalyticsV2Endpoint,
  request?: AnalyticsRequest,
): Promise<TrafficResponse> =>
  fetchResource(endpointBuilder('traffic', viewedUserId, period), request);

export const fetchTrafficLive = (
  viewedUserId: number,
  period: AnalyticsPeriod,
  endpointBuilder = buildAnalyticsV2Endpoint,
  request?: AnalyticsRequest,
): Promise<TrafficLiveResponse> =>
  fetchResource(endpointBuilder('traffic_live', viewedUserId, period), request);

export const fetchUniqueVisitors = (
  viewedUserId: number,
  period: AnalyticsPeriod,
  endpointBuilder = buildAnalyticsV2Endpoint,
  request?: AnalyticsRequest,
): Promise<UniqueVisitorsResponse> =>
  fetchResource(
    endpointBuilder('unique_visitors', viewedUserId, period),
    request,
  );

export const fetchAudience = (
  viewedUserId: number,
  period: AnalyticsPeriod,
  endpointBuilder = buildAnalyticsV2Endpoint,
  request?: AnalyticsRequest,
): Promise<AudienceResponse> =>
  fetchResource(endpointBuilder('audience', viewedUserId, period), request);

export const fetchActivity = (
  viewedUserId: number,
  period: AnalyticsPeriod,
  endpointBuilder = buildAnalyticsV2Endpoint,
  request?: AnalyticsRequest,
): Promise<ActivityResponse> =>
  fetchResource(endpointBuilder('activity', viewedUserId, period), request);

export const fetchReaderTotalCount = (
  viewedUserId: number,
  cacheOnly: boolean,
  options: AnalyticsV2Options = { fixtures: false, delay: false },
  endpointBuilder = buildReaderCountEndpoint,
  request?: AnalyticsRequest,
): Promise<ReaderTotalCountResponse> =>
  fetchResource(
    endpointBuilder('reader_total_count', viewedUserId, cacheOnly, options),
    request,
  );

export const fetchReaderFilteredCounts = (
  viewedUserId: number,
  cacheOnly: boolean,
  options: AnalyticsV2Options = { fixtures: false, delay: false },
  endpointBuilder = buildReaderCountEndpoint,
  request?: AnalyticsRequest,
): Promise<ReaderFilteredCountsResponse> =>
  fetchResource(
    endpointBuilder('reader_filtered_counts', viewedUserId, cacheOnly, options),
    request,
  );

export const fetchReaderCohorts = (
  viewedUserId: number,
  options: AnalyticsV2Options = { fixtures: false, delay: false },
  endpointBuilder = buildReaderCohortEndpoint,
  request?: AnalyticsRequest,
): Promise<ReaderCohortsResponse> =>
  fetchResource(endpointBuilder(viewedUserId, options), request);

export const fetchReaderPreview = (
  viewedUserId: number,
  options: AnalyticsV2Options = { fixtures: false, delay: false },
  endpointBuilder = buildReaderPreviewEndpoint,
  request?: AnalyticsRequest,
): Promise<ReaderPreviewResponse> =>
  fetchResource(endpointBuilder(viewedUserId, options), request);

export const fetchReaderProfiles = (
  viewedUserId: number,
  category: ReaderCategory,
  cursor: string | undefined,
  options: AnalyticsV2Options = { fixtures: false, delay: false },
  endpointBuilder = buildReaderProfilesEndpoint,
  request?: AnalyticsRequest,
): Promise<ReaderProfilesResponse> =>
  fetchResource(
    endpointBuilder(viewedUserId, category, cursor, options),
    request,
  );

export const fetchReaderRankings = (
  viewedUserId: number,
  options: AnalyticsV2Options = { fixtures: false, delay: false },
  endpointBuilder = buildReaderRankingsEndpoint,
  request?: AnalyticsRequest,
): Promise<ReaderRankingsResponse> =>
  fetchResource(endpointBuilder(viewedUserId, options), request);

export const fetchLandingStats = <S extends keyof LandingSections>(
  viewedUserId: number,
  section: S,
  options: AnalyticsV2Options = { fixtures: false, delay: false },
  request?: AnalyticsRequest,
): Promise<LandingSections[S]> =>
  fetchResource(
    routes.landing_stats_api_v0_user_analytics_v2_path({
      user_id: viewedUserId,
      section,
      ...(options.fixtures && options.landingVariant
        ? { landing_variant: options.landingVariant }
        : {}),
      ...(options.fixtures ? { fixtures: true } : {}),
      ...(options.fixtures && options.delay ? { delay: true } : {}),
    }),
    request,
  );

export const followReader = (readerId: number): Promise<void> =>
  fetchWithCsrf(
    routes.api_v0_user_following_index_path({
      user_id: readerId,
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source: 'readers_page',
        source_details: { surface: 'analytics_v2_readers' },
      }),
    },
  ).then((response) => {
    if (!response.ok)
      throw new Error(`Analytics request failed (${response.status})`);
  });
